import { useEffect, useState } from 'react';
import { supabase } from '../services/supabase';

interface PaymentVerificationModalProps {
  isOpen: boolean;
  onSuccess: () => void;
  onTimeout: () => void;
}

/**
 * Modal that shows while waiting for payment to be processed
 * Polls the backend every 3 seconds to check if user has premium
 */
export function PaymentVerificationModal({
  isOpen,
  onSuccess,
  onTimeout,
}: PaymentVerificationModalProps) {
  const [timeElapsed, setTimeElapsed] = useState(0);
  const MAX_WAIT_TIME = 60; // 60 seconds timeout

  useEffect(() => {
    if (!isOpen) return;

    let intervalId: number;
    let timeoutId: number;

    // Function to check payment status
    const checkPaymentStatus = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          console.error('No session found');
          return;
        }

        // Call the check-payment-status function
        const { data, error } = await supabase.functions.invoke('check-payment-status', {
          body: {},
        });

        if (error) {
          console.error('Error checking payment status:', error);
          return;
        }

        console.log('Payment status:', data);

        // If user has premium, trigger success
        if (data?.has_premium) {
          clearInterval(intervalId);
          clearTimeout(timeoutId);
          onSuccess();
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      }
    };

    // Start checking immediately
    checkPaymentStatus();

    // Poll every 3 seconds
    intervalId = window.setInterval(() => {
      checkPaymentStatus();
      setTimeElapsed((prev) => prev + 3);
    }, 3000);

    // Timeout after MAX_WAIT_TIME seconds
    timeoutId = window.setTimeout(() => {
      clearInterval(intervalId);
      onTimeout();
    }, MAX_WAIT_TIME * 1000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [isOpen, onSuccess, onTimeout]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl p-8 max-w-md w-full border border-gray-700">
        <div className="text-center">
          {/* Animated loader */}
          <div className="mb-6 flex justify-center">
            <div className="relative">
              <div className="w-20 h-20 border-4 border-gray-700 rounded-full"></div>
              <div className="w-20 h-20 border-4 border-blue-500 rounded-full animate-spin border-t-transparent absolute top-0 left-0"></div>
            </div>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-white mb-3">
            Verifying Your Payment
          </h2>

          {/* Message */}
          <p className="text-gray-300 mb-6">
            Please wait a few seconds while we confirm your payment with our payment processor.
          </p>

          {/* Time elapsed */}
          <div className="text-sm text-gray-400 mb-4">
            Time elapsed: {timeElapsed}s
          </div>

          {/* Progress indicator */}
          <div className="w-full bg-gray-700 rounded-full h-1.5 mb-4 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-blue-500 to-purple-500 h-1.5 transition-all duration-300 rounded-full"
              style={{ width: `${(timeElapsed / MAX_WAIT_TIME) * 100}%` }}
            ></div>
          </div>

          {/* Helpful message */}
          <p className="text-xs text-gray-500">
            This usually takes just a few seconds...
          </p>
        </div>
      </div>
    </div>
  );
}
